import { useAuth } from "@crm/access/hooks/use-auth";
import { useMemo } from "react";
import type { Permission, Role } from "../types";

/**
 * 权限检查 Hook（重构版 v2）
 *
 * ✅ 直接从 auth context 读取权限（已由 __root.tsx beforeLoad 加载）
 * ✅ 不再依赖 TanStack Query
 * ✅ 性能更好，无需等待异步加载
 *
 * @param permission - 权限或权限数组
 * @returns 是否拥有权限
 */
export function usePermission(permission: Permission | Permission[]): boolean {
  const { user, permissions } = useAuth();

  return useMemo(() => {
    if (!user) {
      return false;
    }

    const perms = Array.isArray(permission) ? permission : [permission];
    return perms.some((p) => permissions.has(p));
  }, [user, permissions, permission]);
}

/**
 * 角色检查 Hook
 *
 * 检查当前用户是否拥有特定角色
 *
 * @param role - 角色或角色数组
 * @returns 是否拥有该角色
 */
export function useRole(role: Role | Role[]): boolean {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
      return false;
    }

    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(user.role);
  }, [user, role]);
}

/**
 * 多权限检查 Hook
 *
 * 返回一个对象，包含多个权限的检查结果
 *
 * @param permissions - 权限对象
 * @returns 权限检查结果对象
 */
export function usePermissions<T extends Record<string, Permission | Permission[]>>(
  perms: T,
): Record<keyof T, boolean> {
  const { user, permissions } = useAuth();

  return useMemo(() => {
    if (!user) {
      return Object.fromEntries(Object.keys(perms).map((key) => [key, false])) as Record<
        keyof T,
        boolean
      >;
    }

    return Object.fromEntries(
      Object.entries(perms).map(([key, permission]) => {
        const permList = Array.isArray(permission) ? permission : [permission];
        return [key, permList.some((p) => permissions.has(p))];
      }),
    ) as Record<keyof T, boolean>;
  }, [user, permissions, perms]);
}
